import { useEffect } from 'react'

/**
 * Modal de confirmación reutilizable para acciones críticas del cliente.
 * Reemplaza window.confirm() / window.alert() para tener UI consistente con la app.
 *
 * Props:
 *   abierto       — bool, controla visibilidad
 *   titulo        — string, encabezado
 *   mensaje       — string | ReactNode, cuerpo
 *   confirmLabel  — string (default "Confirmar")
 *   cancelLabel   — string (default "Cancelar") — si es null, no se muestra (modo info)
 *   variant       — 'default' | 'danger' (default 'default')
 *   procesando    — bool, si true muestra spinner y disabled
 *   onConfirm     — () → void
 *   onCancel      — () → void
 *
 * Uso simple:
 *   <ConfirmModal
 *     abierto={modal === 'pausar'}
 *     titulo="Pausar suscripción"
 *     mensaje="No vamos a hacer entregas ni cobrarte hasta que la reactives."
 *     confirmLabel="Sí, pausar 1 mes"
 *     onConfirm={confirmarPausa}
 *     onCancel={() => setModal(null)}
 *   />
 *
 * Modo "info" (sin Cancelar):
 *   <ConfirmModal abierto={!!msgExito} titulo="¡Listo!" mensaje={msgExito}
 *     confirmLabel="Cerrar" cancelLabel={null} onConfirm={() => setMsgExito(null)} />
 */
export default function ConfirmModal({
  abierto,
  titulo,
  mensaje,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'default',
  procesando = false,
  onConfirm,
  onCancel,
}) {
  // ESC cierra
  useEffect(() => {
    if (!abierto) return
    const onKey = (e) => { if (e.key === 'Escape' && !procesando && onCancel) onCancel() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [abierto, procesando, onCancel])

  if (!abierto) return null

  const isDanger = variant === 'danger'

  return (
    <div className="cm-bg" onClick={() => { if (!procesando && onCancel) onCancel() }}>
      <div className="cm-card" onClick={e => e.stopPropagation()}>
        <h2 className="cm-titulo">{titulo}</h2>
        {mensaje && <div className="cm-mensaje">{mensaje}</div>}
        <div className="cm-acciones">
          {cancelLabel && (
            <button
              type="button"
              className="cm-btn cm-btn-cancelar"
              onClick={onCancel}
              disabled={procesando}
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            className={`cm-btn cm-btn-confirmar${isDanger ? ' cm-btn-danger' : ''}`}
            onClick={onConfirm}
            disabled={procesando}
          >
            {procesando ? <span className="cm-spinner" /> : confirmLabel}
          </button>
        </div>
      </div>
      <style>{`
        .cm-bg {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(15, 23, 42, .65);
          backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          padding: 16px; animation: cmFade .15s ease-out;
        }
        @keyframes cmFade { from { opacity: 0 } to { opacity: 1 } }
        .cm-card {
          background: #fff; border-radius: 16px;
          padding: 24px 24px 20px;
          max-width: 460px; width: 100%;
          box-shadow: 0 20px 60px rgba(15,23,42,.35);
          animation: cmPop .18s ease-out;
        }
        @keyframes cmPop { from { transform: scale(.96); opacity: 0 } to { transform: scale(1); opacity: 1 } }
        .cm-titulo {
          margin: 0 0 10px;
          font-size: 18px; font-weight: 800;
          color: #1A3461; letter-spacing: -.01em;
          line-height: 1.3;
        }
        .cm-mensaje {
          font-size: 14px; line-height: 1.55;
          color: #475569;
          margin-bottom: 20px;
        }
        .cm-mensaje strong { color: #1A3461 }
        .cm-acciones {
          display: flex; justify-content: flex-end;
          gap: 10px; flex-wrap: wrap;
        }
        .cm-btn {
          padding: 11px 18px; border-radius: 10px;
          font-family: inherit; font-size: 14px; font-weight: 700;
          cursor: pointer; border: 0;
          transition: background .15s, transform .1s;
          min-width: 110px;
          display: inline-flex; align-items: center; justify-content: center;
        }
        .cm-btn:disabled { opacity: .55; cursor: not-allowed }
        .cm-btn:active:not(:disabled) { transform: translateY(1px) }
        .cm-btn-cancelar {
          background: #F1F5F9; color: #475569;
        }
        .cm-btn-cancelar:hover:not(:disabled) { background: #E2E8F0 }
        .cm-btn-confirmar {
          background: linear-gradient(135deg, #00A8B5, #1A3461);
          color: #fff;
        }
        .cm-btn-confirmar:hover:not(:disabled) { filter: brightness(1.05) }
        .cm-btn-danger {
          background: linear-gradient(135deg, #DC2626, #991B1B);
        }
        .cm-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: cmSpin .7s linear infinite;
        }
        @keyframes cmSpin { to { transform: rotate(360deg) } }
        @media (max-width: 480px) {
          .cm-acciones { flex-direction: column-reverse }
          .cm-btn { width: 100% }
        }
      `}</style>
    </div>
  )
}
